/**
 * CORE-S-01 (read) + CORE-S-02 (edit) Campaign Settings tab.
 *
 * Chans's 2026-09-15 rebuild spec §8: new left-nav tab, contents in order
 * Locations → Ad Schedule → Extensions. Each panel renders live account
 * state for the current workspace.
 *
 * Shipping state:
 *
 *   - S-01: read surfaces (Locations / Ad Schedule / Extensions tables).
 *   - S-02 phase A: per-row Remove for Locations and Ad Schedule with a
 *     blast-radius typed-confirmation retry.
 *   - S-02.b: add-a-new-slot form for Ad Schedule (this file's
 *     `AddAdScheduleForm`). Chosen first because the day-of-week + hour
 *     picker is bounded — no external autocomplete lookup needed.
 *   - S-02.c (filed): add-a-new-location. Needs a geo-target-constant
 *     search autocomplete UI backed by /geoTargetConstants:search; the
 *     picker work is out of scope for S-02.b.
 *   - Extensions stay view-only per spec §S-02 note.
 *   - S-04: Conversion settings gets a placeholder card at the bottom
 *     naming the primary / secondary conversion-action concept and
 *     pointing at WhatConverts (CORE-W-04) as the hub. Per-campaign
 *     primary / secondary editing lands with S-04.b once the write
 *     endpoint exists.
 *
 * Every write goes through the existing `addAdScheduleSlots` /
 * `removeLocations` / `removeAdScheduleSlots` service, which posts to
 * endpoints that hand off to GoogleAdsWriter.mutate() on the backend
 * (writes audit-log row BEFORE the API call — no bypass, per
 * CLAUDE.md).
 *
 * The blast-radius gate is respected on removes: a remove that would
 * zero out a campaign's targeting comes back as a 409 with a typed-
 * confirmation phrase; the dialog switches to typed-confirmation mode
 * and requires the operator to spell the phrase before the retry lands.
 */
import { fuzzyFilter } from '@/lib/fuzzyMatch';
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TablePagination, usePagination } from '@/components/ui/data-table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  addAdScheduleSlots,
  canWrite,
  getStructure,
  removeAdScheduleSlots,
  removeLocations,
  type BlastRadiusConfirmationDetail,
  type ExtensionRow,
  type LocationWriteResponse,
  type StructureResponse,
  type TargetingRow,
} from '@/services/googleAdsManagementService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { CollapsibleCard } from '@/components/google-ads/CollapsibleCard';
import { useTenantStore } from '@/stores/useTenantStore';

interface CampaignSettingsPageProps {
  tenantId?: string | null;
}

const READ_WINDOW_DAYS = 30;

function useStructure(tenantId: string | null) {
  return useQuery<StructureResponse>({
    queryKey: ['campaign-settings-structure', tenantId, READ_WINDOW_DAYS],
    queryFn: () => getStructure(tenantId ?? '', READ_WINDOW_DAYS),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// Blast-radius parse — matches AdsManagementTab's contract so a remove that
// would zero out a campaign's targeting surfaces its typed-confirmation
// phrase rather than looking like a generic 409.
// ---------------------------------------------------------------------------

function parseBlastRadiusDetail(
  err: unknown,
): BlastRadiusConfirmationDetail | null {
  const response = (
    err as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail as
    | Partial<BlastRadiusConfirmationDetail>
    | undefined;
  if (!detail || detail.code !== 'blast_radius_confirmation_required') {
    return null;
  }
  const reasons = Array.isArray(detail.assessment?.reasons)
    ? detail.assessment.reasons.filter(
        (r): r is string => typeof r === 'string',
      )
    : [];
  return {
    code: 'blast_radius_confirmation_required',
    assessment: {
      requires_confirmation: Boolean(detail.assessment?.requires_confirmation),
      reasons,
      summary: detail.assessment?.summary ?? {},
    },
    confirmation_phrase:
      typeof detail.confirmation_phrase === 'string'
      && detail.confirmation_phrase.trim()
        ? detail.confirmation_phrase
        : 'CONFIRM',
  };
}

function errorMessage(err: unknown): string {
  const responseDetail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof responseDetail === 'string' && responseDetail.trim()) {
    return responseDetail.trim();
  }
  const message = (err as { message?: string })?.message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : 'Could not complete the request.';
}

// ---------------------------------------------------------------------------
// Removal state — one operator action targets one row at a time. If more
// than one row needs removing, the operator repeats. Bulk remove is filed
// as CORE-S-02.b to keep phase A honest.
// ---------------------------------------------------------------------------

type TargetingKind = 'location' | 'schedule';

interface PendingRemoval {
  kind: TargetingKind;
  row: TargetingRow;
  blastRadius: BlastRadiusConfirmationDetail | null;
  errorText: string | null;
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

/**
 * CORE-S-01.b: TargetingTable now groups rows by campaign and renders one
 * campaign per row with the targets as inline chips. The flat "one row per
 * target-campaign pair" shape drowned the operator on any tenant with more
 * than a handful of locations — Prestige has 30+ location rows for its
 * five campaigns.
 *
 * Each chip carries the target's label + (if `canRemove` and the row has a
 * resource_name) a tiny X button that fires the existing per-row Remove
 * flow. The blast-radius dialog is unchanged; only the launch point moved
 * from a table cell to a chip.
 */
export interface CampaignTargetGroup {
  campaignResourceName: string;
  campaignName: string;
  targets: TargetingRow[];
}

export function groupTargetingByCampaign(
  rows: readonly TargetingRow[],
): CampaignTargetGroup[] {
  const byRn = new Map<string, CampaignTargetGroup>();
  for (const row of rows) {
    const key = row.campaign_resource_name || row.campaign_name;
    let group = byRn.get(key);
    if (!group) {
      group = {
        campaignResourceName: row.campaign_resource_name,
        campaignName: row.campaign_name,
        targets: [],
      };
      byRn.set(key, group);
    }
    group.targets.push(row);
  }
  return Array.from(byRn.values()).sort((a, b) =>
    a.campaignName.localeCompare(b.campaignName),
  );
}

const TARGETING_PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// CORE-S-01.h: section-header summary breakdowns.
//
// Each collapsible section's title line ("22 campaigns · 5688 targets")
// used to be a plain string. Operators asked what the numbers meant, so
// each stat now sits inside a hoverable <span title="..."> that names
// the count AND — where useful — enumerates the breakdown (top campaign
// contributors, per-day slot counts, per-type extension counts).
//
// Kept as small typed React components so a hover call-site never
// diverges from the count it is naming.
// ---------------------------------------------------------------------------

function SummaryStat({
  value,
  label,
  title,
}: {
  value: number | string;
  label: string;
  title: string;
}) {
  return (
    <span
      title={title}
      className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-4 hover:decoration-foreground/60"
    >
      {value} {label}
    </span>
  );
}

function LocationsSummary({ rows }: { rows: readonly TargetingRow[] }) {
  const groups = groupTargetingByCampaign(rows);
  const excluded = rows.filter((r) => r.is_negative).length;
  const included = rows.length - excluded;
  // Top campaign contributor for the tooltip.
  const topCampaign = groups
    .slice()
    .sort((a, b) => b.targets.length - a.targets.length)[0];
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <SummaryStat
        value={groups.length}
        label={`campaign${groups.length === 1 ? '' : 's'}`}
        title={
          topCampaign
            ? `Campaigns with any location targeting. Biggest: ${topCampaign.campaignName} (${topCampaign.targets.length} targets).`
            : 'Campaigns with any location targeting.'
        }
      />
      <span className="text-muted-foreground">·</span>
      <SummaryStat
        value={included.toLocaleString()}
        label="included"
        title="Locations where ads CAN serve — positive targeting."
      />
      <span className="text-muted-foreground">·</span>
      <SummaryStat
        value={excluded.toLocaleString()}
        label="excluded"
        title="Locations where ads MUST NOT serve — negative targeting. Big numbers here are Google Ads' way of restricting a local campaign to a small area (exclude everything except X)."
      />
    </span>
  );
}

function AdScheduleSummary({ rows }: { rows: readonly TargetingRow[] }) {
  const groups = groupTargetingByCampaign(rows);
  // Best-effort day breakdown from the label text (each label reads like
  // "Monday 08:00 - 20:00"). Operators asked what the counts meant, so a
  // brief per-day tally in the tooltip helps.
  const dayCounts = new Map<string, number>();
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  for (const r of rows) {
    for (const day of dayNames) {
      if (r.label.startsWith(day)) {
        dayCounts.set(day, (dayCounts.get(day) ?? 0) + 1);
        break;
      }
    }
  }
  const dayBreakdown = dayNames
    .filter((d) => dayCounts.get(d))
    .map((d) => `${d.slice(0, 3)} ${dayCounts.get(d)}`)
    .join(' · ');
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <SummaryStat
        value={groups.length}
        label={`campaign${groups.length === 1 ? '' : 's'}`}
        title="Campaigns with any ad schedule set."
      />
      <span className="text-muted-foreground">·</span>
      <SummaryStat
        value={rows.length}
        label={`slot${rows.length === 1 ? '' : 's'}`}
        title={
          dayBreakdown
            ? `Ad schedule slots across all campaigns. Per day: ${dayBreakdown}. A slot is one day + hour range a campaign is allowed to serve.`
            : 'Ad schedule slots across all campaigns. A slot is one day + hour range a campaign is allowed to serve.'
        }
      />
    </span>
  );
}

function ExtensionsSummary({ rows }: { rows: readonly ExtensionRow[] }) {
  const groups = groupExtensionsByScope(rows);
  // Type breakdown across all scopes for the extensions tooltip.
  const typeCounts = new Map<string, number>();
  for (const r of rows) {
    typeCounts.set(r.extension_type, (typeCounts.get(r.extension_type) ?? 0) + 1);
  }
  const typeBreakdown = Array.from(typeCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([t, n]) => `${n} ${t}${n === 1 ? '' : 's'}`)
    .join(' · ');
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <SummaryStat
        value={groups.length}
        label={`scope${groups.length === 1 ? '' : 's'}`}
        title={
          'Scopes are either "Account-wide" (extension attached to every campaign) or a specific campaign name.'
        }
      />
      <span className="text-muted-foreground">·</span>
      <SummaryStat
        value={rows.length}
        label={`extension${rows.length === 1 ? '' : 's'}`}
        title={
          typeBreakdown
            ? `Extensions attached across all scopes. Breakdown: ${typeBreakdown}. Google picks from these at serve time.`
            : 'Extensions attached across all scopes. Google picks from these at serve time.'
        }
      />
    </span>
  );
}


/** CORE-S-01.f: cap chip preview per row at 5 — the summary columns
 *  (Included / Excluded / Bid mods) already carry the counts, so the
 *  chip preview only needs to give the eye a taste. "Show all N" opens
 *  the modal (CORE-S-01.e) with search + filter for the full list. */
const TARGET_CHIP_CAP = 5;
/** CORE-S-01.c: when this fraction of a row's targets are excluded,
 *  the row is "mostly excluded" — drop the per-chip "−" prefix (which
 *  reads as noise on 500+ chips) and tag the row summary as Excluded
 *  instead. */
const MOSTLY_EXCLUDED_FRACTION = 0.9;

function TargetChip({
  target,
  canRemove,
  onRemove,
  hideNegativeMark = false,
}: {
  target: TargetingRow;
  canRemove: boolean;
  onRemove?: (row: TargetingRow) => void;
  /**
   * CORE-S-01.c: on a row where every target (or ~all) are excluded,
   * the per-chip "−" prefix reads as noise. Set true to drop it — the
   * row-level "Excluded" tag on the campaign column makes the sign
   * self-evident.
   */
  hideNegativeMark?: boolean;
}) {
  const bidText = target.bid_modifier
    ? ` · ${(target.bid_modifier * 100).toFixed(0)}%`
    : '';
  const removable = canRemove && Boolean(target.resource_name);
  return (
    <Badge
      variant="outline"
      className={
        // CORE-S-01.f: dark-mode contrast pass. bg-destructive/5 was
        // ~invisible on dark; step it up to /15 and give text a
        // dark:text-red-200 override so it stays readable on the dark
        // panel background. Light mode keeps the existing tone.
        'inline-flex items-center gap-1.5 border-border text-[11px] font-normal ' +
        (target.is_negative
          ? 'border-destructive/50 bg-destructive/15 text-destructive dark:border-red-700/60 dark:bg-red-950/50 dark:text-red-200'
          : 'bg-muted/40')
      }
    >
      {target.is_negative && !hideNegativeMark ? (
        <span aria-label="Excluded">−</span>
      ) : null}
      {target.label}
      {bidText && (
        <span className="text-[10px] text-muted-foreground">{bidText}</span>
      )}
      {removable && (
        <button
          type="button"
          onClick={() => onRemove?.(target)}
          title={`Remove ${target.label}`}
          aria-label={`Remove ${target.label} from ${target.campaign_name}`}
          className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-muted-foreground opacity-70 hover:bg-destructive/10 hover:text-destructive hover:opacity-100"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </Badge>
  );
}

/**
 * CORE-S-01.c: the target list cell inside a campaign row. Caps chip
 * display at `TARGET_CHIP_CAP` and offers a "Show all N" toggle for the
 * remainder — a campaign with 5688 excluded countries no longer paints
 * 5688 chips on first render.
 */
function TargetChipList({
  targets,
  canRemove,
  onRemove,
  mostlyExcluded,
  campaignName,
}: {
  targets: readonly TargetingRow[];
  canRemove: boolean;
  onRemove?: (row: TargetingRow) => void;
  mostlyExcluded: boolean;
  campaignName: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const overflow = targets.length - TARGET_CHIP_CAP;
  const visible = targets.slice(0, TARGET_CHIP_CAP);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((t) => (
        <TargetChip
          key={t.resource_name || `${t.campaign_name}|${t.label}`}
          target={t}
          canRemove={canRemove}
          onRemove={onRemove}
          hideNegativeMark={mostlyExcluded}
        />
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="text-[11px] font-medium text-coral-dark hover:underline"
          aria-haspopup="dialog"
        >
          Show all {targets.length}
        </button>
      )}
      {modalOpen && (
        <TargetListModal
          campaignName={campaignName}
          targets={targets}
          canRemove={canRemove}
          onRemove={onRemove}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * CORE-S-01.e: full-list modal opened from "Show all N". A campaign
 * with 5000+ targets is unbrowsable inline; the modal gives an
 * operator a focused, scrollable, searchable view with an
 * Included / Excluded toggle so they can spot-check a specific
 * target quickly.
 *
 * Per-chip Remove routes back through the parent's onRemove — the
 * blast-radius flow is unchanged, only the launch point is different.
 * The modal closes on Cancel / X or when the operator clicks the
 * backdrop; the pending Remove dialog opens on top of it via its
 * own portal so the operator does not lose the modal context.
 */
type ChipFilter = 'all' | 'included' | 'excluded';

function TargetListModal({
  campaignName,
  targets,
  canRemove,
  onRemove,
  onClose,
}: {
  campaignName: string;
  targets: readonly TargetingRow[];
  canRemove: boolean;
  onRemove?: (row: TargetingRow) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ChipFilter>('all');

  const includedCount = targets.filter((t) => !t.is_negative).length;
  const excludedCount = targets.length - includedCount;

  // Fuzzy search, best match first (lib/fuzzyMatch); the list has no user sort.
  const filtered = useMemo(() => {
    const byChip = targets.filter((t) => {
      if (filter === 'included' && t.is_negative) return false;
      if (filter === 'excluded' && !t.is_negative) return false;
      return true;
    });
    return fuzzyFilter(byChip, query, (t) => [t.label]);
  }, [targets, query, filter]);

  return (
    <Dialog open onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Targets — {campaignName}</DialogTitle>
          <DialogDescription>
            {targets.length.toLocaleString()} target
            {targets.length === 1 ? '' : 's'}
            {includedCount > 0 && excludedCount > 0
              ? ` · ${includedCount.toLocaleString()} included · ${excludedCount.toLocaleString()} excluded`
              : includedCount > 0
                ? ` · all included`
                : ` · all excluded`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="search"
            placeholder="Search targets…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 flex-1 min-w-[220px]"
            aria-label="Search targets"
          />
          <div className="flex items-center gap-1 rounded-md border border-border p-1 text-xs">
            {(['all', 'included', 'excluded'] as ChipFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={
                  'rounded px-2 py-1 font-medium capitalize ' +
                  (filter === f
                    ? 'bg-coral text-white'
                    : 'text-muted-foreground hover:text-foreground')
                }
                aria-pressed={filter === f}
              >
                {f === 'all'
                  ? `All (${targets.length.toLocaleString()})`
                  : f === 'included'
                    ? `Included (${includedCount.toLocaleString()})`
                    : `Excluded (${excludedCount.toLocaleString()})`}
              </button>
            ))}
          </div>
        </div>

        <div
          className="max-h-[60vh] overflow-y-auto rounded-md border border-border p-3"
          data-testid="target-list-modal-body"
        >
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No targets match this filter.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {filtered.map((t) => (
                <TargetChip
                  key={t.resource_name || `${t.campaign_name}|${t.label}`}
                  target={t}
                  canRemove={canRemove}
                  onRemove={onRemove}
                  hideNegativeMark={false}
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex w-full items-center justify-between text-xs text-muted-foreground">
            <span>
              Showing {filtered.length.toLocaleString()} of{' '}
              {targets.length.toLocaleString()}
            </span>
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TargetingTable({
  rows,
  emptyText,
  canRemove,
  onRemove,
}: {
  rows: readonly TargetingRow[];
  emptyText: string;
  canRemove: boolean;
  onRemove?: (row: TargetingRow) => void;
}) {
  const groups = useMemo(() => groupTargetingByCampaign(rows), [rows]);
  const pagination = usePagination(groups.length, {
    defaultPageSize: TARGETING_PAGE_SIZE,
  });
  const { page, pageSize, setPage, setPageSize, pageCount, slice } = pagination;
  const pageGroups = slice(groups);

  if (groups.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Campaign</th>
              <th className="px-3 py-2 text-right font-semibold">Included</th>
              <th className="px-3 py-2 text-right font-semibold">Excluded</th>
              <th className="px-3 py-2 text-left font-semibold">Bid mods</th>
              <th className="px-3 py-2 text-left font-semibold">Targets</th>
            </tr>
          </thead>
          <tbody>
            {pageGroups.map((group) => {
              const excludedCount = group.targets.filter(
                (t) => t.is_negative,
              ).length;
              const includedCount = group.targets.length - excludedCount;
              const mostlyExcluded =
                excludedCount / group.targets.length >= MOSTLY_EXCLUDED_FRACTION;
              // Bid-mods column summary: count of targets with a
              // non-zero bid_modifier + min/max range so the operator
              // can see at a glance whether a campaign has any
              // adjustments.
              const bidMods = group.targets.filter(
                (t) => Boolean(t.bid_modifier),
              );
              const bidPercents = bidMods.map((t) => t.bid_modifier * 100);
              const bidSummary = bidMods.length === 0
                ? '—'
                : bidMods.length === 1
                  ? `${bidPercents[0].toFixed(0)}%`
                  : `${bidMods.length} · ${Math.min(...bidPercents).toFixed(0)}% – ${Math.max(...bidPercents).toFixed(0)}%`;
              return (
                <tr
                  key={group.campaignResourceName || group.campaignName}
                  className="border-b border-border/60 last:border-0 align-top hover:bg-muted/20"
                >
                  <td className="px-3 py-2 font-medium">
                    {group.campaignName}
                    {mostlyExcluded && (
                      <div className="mt-0.5">
                        <Badge
                          variant="outline"
                          className="border-destructive/50 bg-destructive/15 text-[10px] font-normal text-destructive dark:border-red-700/60 dark:bg-red-950/50 dark:text-red-200"
                          title={`${excludedCount} of ${group.targets.length} targets are excluded — the rest is what serves.`}
                        >
                          Mostly excluded
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {includedCount === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      includedCount.toLocaleString()
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {excludedCount === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span
                        className={
                          mostlyExcluded
                            ? 'text-destructive dark:text-red-300'
                            : ''
                        }
                      >
                        {excludedCount.toLocaleString()}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground tabular-nums">
                    {bidSummary}
                  </td>
                  <td className="px-3 py-2">
                    <TargetChipList
                      targets={group.targets}
                      canRemove={canRemove}
                      onRemove={onRemove}
                      mostlyExcluded={mostlyExcluded}
                      campaignName={group.campaignName}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {groups.length > pageSize && (
        <TablePagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalRows={groups.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 25, 50]}
        />
      )}
    </div>
  );
}

/**
 * CORE-S-01.b: ExtensionsTable groups extensions by scope (campaign name
 * or "Account-wide") so a scope with many extensions collapses into a
 * single row with a per-type chip list, matching the Locations / Ad
 * schedule layout. Content is rendered as a comma-joined list inside
 * each chip's tooltip so the operator can spot-check without scrolling
 * a long flat table.
 *
 * View-only per spec §S-02 — no remove buttons, no chip X.
 */
export interface ExtensionScopeGroup {
  scope: string;
  byType: Map<string, ExtensionRow[]>;
  total: number;
}

export function groupExtensionsByScope(
  rows: readonly ExtensionRow[],
): ExtensionScopeGroup[] {
  const byScope = new Map<string, ExtensionScopeGroup>();
  for (const row of rows) {
    const key = row.scope || 'Account-wide';
    let group = byScope.get(key);
    if (!group) {
      group = { scope: key, byType: new Map(), total: 0 };
      byScope.set(key, group);
    }
    const bucket = group.byType.get(row.extension_type) ?? [];
    bucket.push(row);
    group.byType.set(row.extension_type, bucket);
    group.total += 1;
  }
  return Array.from(byScope.values()).sort((a, b) => {
    // "Account-wide" first, then alphabetical.
    if (a.scope === 'Account-wide' && b.scope !== 'Account-wide') return -1;
    if (b.scope === 'Account-wide' && a.scope !== 'Account-wide') return 1;
    return a.scope.localeCompare(b.scope);
  });
}

const EXTENSIONS_PAGE_SIZE = 10;

function ExtensionsTable({ rows }: { rows: readonly ExtensionRow[] }) {
  const groups = useMemo(() => groupExtensionsByScope(rows), [rows]);
  const pagination = usePagination(groups.length, {
    defaultPageSize: EXTENSIONS_PAGE_SIZE,
  });
  const { page, pageSize, setPage, setPageSize, pageCount, slice } = pagination;
  const pageGroups = slice(groups);

  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No sitelinks, callouts, or other extensions on this account.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Scope</th>
              <th className="px-3 py-2 text-left font-semibold">Extensions</th>
            </tr>
          </thead>
          <tbody>
            {pageGroups.map((group) => (
              <tr
                key={group.scope}
                className="border-b border-border/60 last:border-0 align-top hover:bg-muted/20"
              >
                <td className="px-3 py-2 font-medium">
                  {group.scope}
                  <div className="mt-0.5 text-[11px] font-normal text-muted-foreground">
                    {group.total} extension{group.total === 1 ? '' : 's'}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1.5">
                    {Array.from(group.byType.entries()).map(([type, items]) => {
                      // CORE-S-01.g: separator between type and count so the
                      // chip reads as "Sitelink · 5" instead of "Sitelink 5"
                      // (which read as a code). aria-label spells the count
                      // out ("5 Sitelink extensions") so a screen reader
                      // does not read "Sitelink five" as a name. Title
                      // tooltip carries both the count meaning + the content
                      // list so an operator hovering learns what the number
                      // is a menu of.
                      const plural = items.length === 1 ? '' : 's';
                      return (
                        <Badge
                          key={type}
                          variant="outline"
                          className="inline-flex items-center gap-1 border-border bg-muted/40 text-[11px] font-normal"
                          aria-label={`${items.length} ${type} extension${plural}`}
                          title={
                            `${items.length} ${type} extension${plural} attached — ` +
                            `Google picks from these when it shows an ad.\n\n` +
                            items.map((i) => `• ${i.content}`).join('\n')
                          }
                        >
                          {type}
                          <span className="text-muted-foreground">·</span>
                          <span className="font-mono">{items.length}</span>
                        </Badge>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {groups.length > pageSize && (
        <TablePagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalRows={groups.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 25, 50]}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm dialog. Two modes:
//   1. Simple confirm (first click). Cancel / Remove buttons only.
//   2. Blast-radius mode (after a 409 comes back). Adds the reasons list
//      and a typed-input for the confirmation phrase; Remove is disabled
//      until the phrase matches exactly.
// ---------------------------------------------------------------------------

function ConfirmRemoveDialog({
  pending,
  onCancel,
  onConfirm,
  typedText,
  onTypedTextChange,
}: {
  pending: PendingRemoval;
  onCancel: () => void;
  onConfirm: (confirmationText?: string) => void;
  typedText: string;
  onTypedTextChange: (v: string) => void;
}) {
  const noun = pending.kind === 'location' ? 'location' : 'schedule slot';
  const blast = pending.blastRadius;
  const phraseMatches =
    blast !== null && typedText.trim() === blast.confirmation_phrase;

  return (
    <Dialog open onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {noun}?</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{pending.row.label}</span>{' '}
            on{' '}
            <span className="font-medium text-foreground">
              {pending.row.campaign_name}
            </span>
            . This is a Google Ads write unless the backend deploy is running
            in dry-run mode.
          </DialogDescription>
        </DialogHeader>

        {pending.errorText && !blast && (
          <p className="text-sm text-destructive">{pending.errorText}</p>
        )}

        {blast && (
          <div className="space-y-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 flex-none" />
              Additional confirmation required (blast-radius safety gate)
            </div>
            {blast.assessment.reasons.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-xs">
                {blast.assessment.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
            <div className="space-y-1">
              <Input
                value={typedText}
                onChange={(e) => onTypedTextChange(e.target.value)}
                placeholder={blast.confirmation_phrase}
                className="bg-card"
                autoFocus
              />
              <p className="text-xs text-amber-900/80 dark:text-amber-300/80">
                Type{' '}
                <code className="rounded bg-card px-1 py-0.5 text-[11px]">
                  {blast.confirmation_phrase}
                </code>{' '}
                exactly, then confirm.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={false}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(blast ? typedText.trim() : undefined)}
            disabled={Boolean(blast) && !phraseMatches}
          >
            {blast ? 'Confirm and remove' : 'Confirm remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CORE-S-02.b — Add-a-new-row for Ad Schedule.
//
// Chans's spec §CORE-S-02: "Make settings editable with writeback to Google
// Ads." Phase A (Remove) shipped in the parent CORE-S-02. Phase B adds the
// half that makes the tab truly editable — an operator can add a schedule
// slot to a campaign without leaving Core.
//
// Scope guardrails:
//   - Ad Schedule only. Location add needs a geo-target-constant lookup
//     UI (autocomplete backed by /geoTargetConstants:search); that lands
//     as CORE-S-02.c so this phase does not stall on the picker work.
//   - Extensions stay view-only per spec §S-02 note.
//   - Writes go through addAdScheduleSlots, which posts to the audited
//     /google-ads/ad-schedule-control/add endpoint. Every mutate is a
//     GoogleAdsWriter.mutate() call server-side (audit-log row BEFORE
//     the API call, per CLAUDE.md).
// ---------------------------------------------------------------------------

const DAY_OF_WEEK_OPTIONS: readonly {
  value:
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';
  label: string;
}[] = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

type DayOfWeek = (typeof DAY_OF_WEEK_OPTIONS)[number]['value'];

interface AddAdScheduleFormProps {
  tenantId: string;
  /** Every campaign the read returned, deduplicated. Empty when the
   *  account has no live campaigns; the form disables the submit in
   *  that case with a plain-English note instead of pretending. */
  campaigns: readonly { name: string; resource_name: string }[];
  onAdded: () => void;
}

function AddAdScheduleForm({
  tenantId,
  campaigns,
  onAdded,
}: AddAdScheduleFormProps) {
  const [campaignResourceName, setCampaignResourceName] = useState<string>(
    campaigns[0]?.resource_name ?? '',
  );
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek>('MONDAY');
  const [startHour, setStartHour] = useState<number>(9);
  const [endHour, setEndHour] = useState<number>(17);

  // Google's ad schedule uses [startHour, endHour) where each is 0..24.
  // Validation: start must be < end; both in-range. A "24" start-hour
  // would be a same-day loop with no window.
  const rangeInvalid =
    !Number.isFinite(startHour) ||
    !Number.isFinite(endHour) ||
    startHour < 0 ||
    startHour > 23 ||
    endHour < 1 ||
    endHour > 24 ||
    startHour >= endHour;

  const mutation = useMutation({
    mutationFn: async () => {
      return addAdScheduleSlots(tenantId, [
        {
          campaignResourceName,
          dayOfWeek,
          startHour,
          endHour,
        },
      ]);
    },
    onSuccess: (res) => {
      if (res.failed?.length) {
        toast.warning(res.message, {
          description: `${res.failed.length} slot did not save.`,
        });
      } else {
        toast.success(res.message || 'Ad-schedule slot added.');
      }
      onAdded();
    },
    onError: (err) => {
      toast.error(errorMessage(err));
    },
  });

  if (campaigns.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground">
        No campaigns to add a schedule to yet. Add a schedule after a
        campaign is created.
      </p>
    );
  }

  return (
    <form
      className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-muted/10 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!campaignResourceName || rangeInvalid || mutation.isPending) return;
        mutation.mutate();
      }}
    >
      <label className="flex min-w-[180px] flex-col gap-1 text-xs">
        <span className="font-medium text-muted-foreground">Campaign</span>
        <Select
          value={campaignResourceName}
          onValueChange={setCampaignResourceName}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Pick a campaign" />
          </SelectTrigger>
          <SelectContent>
            {campaigns.map((c) => (
              <SelectItem key={c.resource_name} value={c.resource_name}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-muted-foreground">Day</span>
        <Select
          value={dayOfWeek}
          onValueChange={(v) => setDayOfWeek(v as DayOfWeek)}
        >
          <SelectTrigger aria-label="Day of week" className="h-9 min-w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAY_OF_WEEK_OPTIONS.map((d) => (
              <SelectItem key={d.value} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-muted-foreground">Start hour</span>
        <Input
          type="number"
          min={0}
          max={23}
          step={1}
          value={startHour}
          onChange={(e) => setStartHour(Number(e.target.value))}
          aria-label="Start hour (0-23)"
          className="h-9 w-20"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-muted-foreground">End hour</span>
        <Input
          type="number"
          min={1}
          max={24}
          step={1}
          value={endHour}
          onChange={(e) => setEndHour(Number(e.target.value))}
          aria-label="End hour (1-24)"
          className="h-9 w-20"
        />
      </label>

      <Button
        type="submit"
        disabled={
          !campaignResourceName || rangeInvalid || mutation.isPending
        }
        className="h-9"
      >
        {mutation.isPending ? (
          <>
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            Adding…
          </>
        ) : (
          'Add slot'
        )}
      </Button>

      {rangeInvalid && (
        <p className="basis-full text-xs text-destructive">
          Start hour must be earlier than end hour; start 0-23, end 1-24.
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CampaignSettingsPage({ tenantId: tenantIdProp }: CampaignSettingsPageProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;
  const q = useStructure(tenantId);
  const queryClient = useQueryClient();

  const [pending, setPending] = useState<PendingRemoval | null>(null);
  const [typedText, setTypedText] = useState('');

  const canRemoveLocation = canWrite(
    q.data?.capabilities,
    'location_targeting_write',
  );
  const canRemoveSchedule = canWrite(
    q.data?.capabilities,
    'ad_schedule_write',
  );

  const removeMutation = useMutation<
    LocationWriteResponse,
    unknown,
    { kind: TargetingKind; resourceName: string; confirmationText?: string }
  >({
    mutationFn: async ({ kind, resourceName, confirmationText }) => {
      if (!tenantId) throw new Error('No tenant');
      if (kind === 'location') {
        return removeLocations(tenantId, [resourceName], confirmationText);
      }
      return removeAdScheduleSlots(tenantId, [resourceName], confirmationText);
    },
    onSuccess: () => {
      setPending(null);
      setTypedText('');
      void queryClient.invalidateQueries({
        queryKey: ['campaign-settings-structure', tenantId, READ_WINDOW_DAYS],
      });
    },
    onError: (err) => {
      const blast = parseBlastRadiusDetail(err);
      const msg = errorMessage(err);
      setPending((prev) =>
        prev ? { ...prev, blastRadius: blast, errorText: blast ? null : msg } : prev,
      );
      setTypedText('');
    },
  });

  const openRemove = useCallback((kind: TargetingKind, row: TargetingRow) => {
    setPending({ kind, row, blastRadius: null, errorText: null });
    setTypedText('');
  }, []);

  const confirmRemove = useCallback(
    (confirmationText?: string) => {
      if (!pending?.row.resource_name) return;
      removeMutation.mutate({
        kind: pending.kind,
        resourceName: pending.row.resource_name,
        confirmationText,
      });
    },
    [pending, removeMutation],
  );

  const cancelRemove = useCallback(() => {
    if (removeMutation.isPending) return;
    setPending(null);
    setTypedText('');
  }, [removeMutation.isPending]);

  const locations = useMemo(() => q.data?.locations ?? [], [q.data]);
  const schedules = useMemo(() => q.data?.ad_schedules ?? [], [q.data]);
  const extensions = useMemo(() => q.data?.extensions ?? [], [q.data]);

  // CORE-S-02.b: the add-schedule form needs the unique campaign list.
  // Deduplicate across the structure's targeting rows so a campaign that
  // has no schedule yet is still a picker option — that is the whole
  // point of "add a new one." Sorted by name for scan.
  const campaignsForAddForm = useMemo(() => {
    if (!q.data) return [] as { name: string; resource_name: string }[];
    const byResource = new Map<string, string>();
    for (const row of q.data.locations) {
      if (row.campaign_resource_name) {
        byResource.set(row.campaign_resource_name, row.campaign_name);
      }
    }
    for (const row of q.data.ad_schedules) {
      if (row.campaign_resource_name) {
        byResource.set(row.campaign_resource_name, row.campaign_name);
      }
    }
    return Array.from(byResource, ([resource_name, name]) => ({
      resource_name,
      name,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [q.data]);

  if (!tenantId) {
    return (
      <div className="space-y-4">
        {/* CORE-S-01.c: purpose statement lives on the outer
            GoogleAdsOpsPage wrapper (renders one H1 + description per
            section for every Ads Tools tab). No inline duplicate here. */}
        <Card>
          <CardHeader>
            <CardTitle>No workspace selected</CardTitle>
            <CardDescription>
              Pick a workspace to see its campaign settings.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        {/* CORE-S-01.c: purpose statement removed — outer wrapper carries it. */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading account structure for {tenantId}...
        </div>
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="space-y-4">
        {/* CORE-S-01.c: purpose statement removed — outer wrapper carries it. */}
        <Card>
          <CardHeader>
            <CardTitle>Could not read campaign settings</CardTitle>
            <CardDescription>
              Core could not reach the Google Ads structure read for{' '}
              <span className="font-medium text-foreground">{tenantId}</span>.
              Check the connection on the Account Audit tab, then reload.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CORE-S-01.c: purpose statement removed here too — the outer
          GoogleAdsOpsPage wrapper (GoogleAdsOpsPage.tsx:9127) already
          renders a description subtitle for every Ads Tools section,
          so this file used to render a THIRD title-ish block below
          it. That was the "repeated header" Jacob flagged on the
          screenshot after S-01.b. */}

      {/* CORE-S-01.b: each section is a CollapsibleCard so an operator
          who is only looking at Extensions can fold Locations + Ad
          Schedule away. Open/closed state is per-viewer localStorage,
          same primitive the legacy Management page uses. */}
      <CollapsibleCard
        id="campaign-settings-locations"
        title="Locations"
        description="Where each campaign is targeted. Empty means the account default."
        summary={
          locations.length > 0 ? (
            <LocationsSummary rows={locations} />
          ) : (
            'account default'
          )
        }
      >
        <TargetingTable
          rows={locations}
          emptyText="No location targeting on any campaign — using the account default."
          canRemove={canRemoveLocation}
          onRemove={(row) => openRemove('location', row)}
        />
      </CollapsibleCard>

      <CollapsibleCard
        id="campaign-settings-ad-schedule"
        title="Ad schedule"
        description="When each campaign is allowed to serve. Empty means all week, all day."
        summary={
          schedules.length > 0 ? (
            <AdScheduleSummary rows={schedules} />
          ) : (
            'always on'
          )
        }
      >
        <div className="space-y-3">
          {/* CORE-S-02.b: add-a-new-schedule form. Rendered only when
              the server grants ad_schedule_write — same capability that
              gates the per-row Remove chips below. */}
          {canRemoveSchedule && (
            <AddAdScheduleForm
              tenantId={tenantId}
              campaigns={campaignsForAddForm}
              onAdded={() =>
                queryClient.invalidateQueries({
                  queryKey: ['campaign-settings-structure', tenantId, READ_WINDOW_DAYS],
                })
              }
            />
          )}
          <TargetingTable
            rows={schedules}
            emptyText="No ad schedule set — these campaigns can serve at any time."
            canRemove={canRemoveSchedule}
            onRemove={(row) => openRemove('schedule', row)}
          />
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        id="campaign-settings-extensions"
        title="Extensions"
        description="Sitelinks, callouts, structured snippets, calls, promotions, lead forms and price — account-wide or attached to a specific campaign."
        summary={
          extensions.length > 0 ? (
            <ExtensionsSummary rows={extensions} />
          ) : (
            'none'
          )
        }
      >
        <ExtensionsTable rows={extensions} />
      </CollapsibleCard>

      {/* CORE-S-04: Conversion settings — primary vs. secondary conversion
          action — get a placeholder home here per Chans's spec §8. The
          hub for conversions data is WhatConverts (CORE-W-04); the
          editing UX lands later. This card is intentionally a note-only
          block so an operator with a config question knows where to
          go without expecting the setting to be editable here today. */}
      <CollapsibleCard
        id="campaign-settings-conversion"
        title="Conversion settings"
        summary={
          <Badge variant="outline" className="text-[10px] font-normal">
            Placeholder
          </Badge>
        }
        description="Primary vs. secondary conversion action — the setting bidding optimises against. Live view + annotate live on the WhatConverts tab (CORE-W-04); editing which action is primary lands here later."
      >
        <div className="space-y-2 text-sm">
          <p className="text-muted-foreground">
            Configured during the campaign build. Change requests today
            go through Google Ads directly; Core picks them up on the
            next read.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            <li>
              Live view of every conversion action, with the primary vs.
              secondary state and a per-row Annotate note, lives on
              WhatConverts (CORE-W-04).
            </li>
            <li>
              Making the primary vs. secondary state editable from Core
              is the next follow-up: CORE-S-04.b. It waits on the
              conversion-action write endpoint, which today only
              annotates.
            </li>
          </ul>
          <div>
            <a
              href="/ads-tools/whatconverts?platform=google-ads"
              className="text-xs font-medium text-coral-dark hover:underline"
            >
              Open WhatConverts to view conversion actions →
            </a>
          </div>
        </div>
      </CollapsibleCard>

      <p className="text-xs text-muted-foreground">
        Ad Schedule supports per-row Add and Remove (CORE-S-02.b).
        Locations support per-row Remove; add-a-new-location lands with
        CORE-S-02.c once the geo-target-constant lookup UI is in.
        Extensions stay view-only for MVP (spec S-02 note). Conversion
        settings live on WhatConverts today; per-campaign primary /
        secondary editing lands with CORE-S-04.b.
      </p>

      {pending && (
        <ConfirmRemoveDialog
          pending={pending}
          onCancel={cancelRemove}
          onConfirm={confirmRemove}
          typedText={typedText}
          onTypedTextChange={setTypedText}
        />
      )}
    </div>
  );
}

export default CampaignSettingsPage;
